import { resolveStorageUrl } from './storageUrl';

const cache: Record<string, HTMLImageElement> = {};
const pending: Record<string, Promise<HTMLImageElement>> = {};
/** Resolved URLs that failed to load — skip retries to avoid error spam / uncaught rejections. */
const failed = new Set<string>();

type ScreenLike = {
  background?: { type?: string; imageUrl?: string };
  layoutItems?: Array<{
    type?: string;
    imageUrl?: string;
    background?: { type?: string; imageUrl?: string };
  }>;
};

function isReady(img: HTMLImageElement | undefined): img is HTMLImageElement {
  return Boolean(img && img.complete && img.naturalWidth > 0);
}

function loadImageElement(resolved: string, useCors: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    if (useCors) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load image: ${resolved}`));
    img.src = resolved;
  });
}

/** Loads a screen image ref (storage path, data URL, or http URL) into the shared cache. */
export async function loadScreenImage(ref: string): Promise<HTMLImageElement> {
  const resolved = resolveStorageUrl(ref);
  if (!resolved) {
    throw new Error('empty image ref');
  }
  if (failed.has(resolved)) {
    throw new Error(`failed to load image: ${resolved}`);
  }
  const cached = cache[resolved];
  if (isReady(cached)) return cached;

  const inFlight = pending[resolved];
  if (inFlight) return inFlight;

  pending[resolved] = (async () => {
    try {
      const img = await loadImageElement(resolved, true);
      cache[resolved] = img;
      return img;
    } catch {
      // CORS may block anonymous load; retry without crossOrigin for canvas preview.
      try {
        const img = await loadImageElement(resolved, false);
        cache[resolved] = img;
        return img;
      } catch (err) {
        failed.add(resolved);
        throw err;
      }
    } finally {
      delete pending[resolved];
    }
  })();

  return pending[resolved];
}

/** Returns a cached image if fully loaded, otherwise null. */
export function getCachedScreenImage(ref: string): HTMLImageElement | null {
  const resolved = resolveStorageUrl(ref);
  if (!resolved) return null;
  const img = cache[resolved];
  return isReady(img) ? img : null;
}

/** Collects unique image refs from screen payloads for preloading. */
export function collectImageRefsFromScreens(screens: ScreenLike[]): string[] {
  const seen = new Set<string>();
  const refs: string[] = [];
  const add = (ref?: string) => {
    const r = (ref || '').trim();
    if (!r || seen.has(r)) return;
    seen.add(r);
    refs.push(r);
  };

  for (const screen of screens) {
    if (screen.background?.type === 'image') add(screen.background.imageUrl);
    for (const item of screen.layoutItems ?? []) {
      if (item.type === 'image') add(item.imageUrl);
      if (item.type === 'device' && item.background?.type === 'image') add(item.background.imageUrl);
    }
  }
  return refs;
}

/** Preloads all image refs; failures are ignored so one bad image does not block others. */
export async function preloadScreenImages(refs: string[]): Promise<void> {
  await Promise.allSettled(refs.map(loadScreenImage));
}
